// Build a customer-facing UPI payment poster from the QR <svg> rendered inside
// `containerId` and download it as a JPEG. The poster (meant to be printed and
// stuck on the charger) is laid out as:
//
//        [ Amik logo ]
//       Scan & Pay via UPI        <- title
//      ┌──────────────┐
//      │   QR  code    │
//      └──────────────┘
//        <caption lines>          <- charger name / id / connector / address
//
// options:
//   logoUrl:      string                       -> image drawn at the top
//   title:        string                        -> heading drawn above the QR
//   captionLines: Array<string | {text, bold, size}> -> details below the QR
//                 (long lines such as the address are word-wrapped)
const POSTER_WIDTH = 400;
const PADDING = 24;

// Word-wrap `text` to `maxWidth` using the currently-set ctx.font.
const wrapText = (ctx, text, maxWidth) => {
  const words = `${text}`.split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${current} ${words[i]}`;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
};

export const downloadQrFromContainer = (containerId, fileName, options = {}) => {
  const { logoUrl, title, captionLines = [] } = options;

  const container = document.getElementById(containerId);
  const svg = container?.querySelector("svg");
  if (!svg) return;

  // Normalize caption lines into { text, bold, size } objects.
  const captions = (Array.isArray(captionLines) ? captionLines : [captionLines])
    .map((line) => (typeof line === "string" ? { text: line } : line))
    .filter((line) => line && line.text !== undefined && `${line.text}` !== "")
    .map((line) => ({
      text: `${line.text}`,
      bold: Boolean(line.bold),
      size: line.size || 16,
    }));

  const svgData = new XMLSerializer().serializeToString(svg);
  const qrSrc = `data:image/svg+xml;base64,${btoa(
    unescape(encodeURIComponent(svgData))
  )}`;

  const compose = (logoImg) => {
    const qrImg = new Image();
    qrImg.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = POSTER_WIDTH;
      const maxTextWidth = POSTER_WIDTH - PADDING * 2;

      const fontFor = (size, bold) =>
        `${bold ? "bold " : ""}${size}px Arial, sans-serif`;

      // ---- Measure first (so we can size the canvas), then draw. ----
      const logoH = logoImg ? 70 : 0;
      let logoW = 0;
      if (logoImg) {
        const ratio =
          logoImg.naturalWidth && logoImg.naturalHeight
            ? logoImg.naturalWidth / logoImg.naturalHeight
            : 3;
        logoW = Math.min(maxTextWidth, logoH * ratio);
      }

      const titleSize = 20;
      const lineGap = 8;

      // Wrap each caption line and record its rendered height.
      const wrapped = captions.map((c) => {
        ctx.font = fontFor(c.size, c.bold);
        return { ...c, lines: wrapText(ctx, c.text, maxTextWidth) };
      });

      let y = PADDING;
      if (logoImg) y += logoH + 16;
      if (title) y += titleSize + 14;
      const qrTop = y;
      y += qrImg.height + 18;
      const captionTop = y;
      wrapped.forEach((c) => {
        c.lines.forEach(() => {
          y += c.size + lineGap;
        });
      });
      const totalHeight = y + PADDING;

      // Setting height resets the context, so set dimensions, then draw.
      canvas.height = totalHeight;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";

      let drawY = PADDING;
      if (logoImg) {
        ctx.drawImage(logoImg, (canvas.width - logoW) / 2, drawY, logoW, logoH);
        drawY += logoH + 16;
      }
      if (title) {
        ctx.fillStyle = "#0b6b3a";
        ctx.font = fontFor(titleSize, true);
        ctx.fillText(title, canvas.width / 2, drawY);
        drawY += titleSize + 14;
      }
      // QR
      ctx.drawImage(qrImg, (canvas.width - qrImg.width) / 2, qrTop);
      // Captions
      let cy = captionTop;
      ctx.fillStyle = "#111111";
      wrapped.forEach((c) => {
        ctx.font = fontFor(c.size, c.bold);
        c.lines.forEach((ln) => {
          ctx.fillText(ln, canvas.width / 2, cy);
          cy += c.size + lineGap;
        });
      });

      const pngFile = canvas.toDataURL("image/jpeg", 0.95);
      const link = document.createElement("a");
      link.download = `${fileName}.jpeg`;
      link.href = pngFile;
      link.click();
    };
    qrImg.src = qrSrc;
  };

  if (logoUrl) {
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    logoImg.onload = () => compose(logoImg);
    // If the logo fails to load, still download the poster without it.
    logoImg.onerror = () => compose(null);
    logoImg.src = logoUrl;
  } else {
    compose(null);
  }
};

// Turn an arbitrary string into a DOM-id-safe fragment.
export const toSafeId = (value) => String(value).replace(/[^a-zA-Z0-9]/g, "");

// Amik logo used for branding downloaded QR posters.
export const AMIK_LOGO_URL = "/logo/amikCharge.jpg";

// Load an image URL into a JPEG data URL (for embedding in jsPDF/canvas).
// Resolves null on failure so callers can proceed without the image.
export const loadImageDataUrl = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 200;
        canvas.height = img.naturalHeight || 80;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          dataUrl: canvas.toDataURL("image/jpeg", 0.92),
          width: canvas.width,
          height: canvas.height,
        });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
